import { ApiClientError, ApiClientTransportError } from '@contract-analyzer/api-client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * Remounted at the authenticated tenant boundary; grid snapshots keep their separate lifetime.
 * Demo mode changes what the API returns without changing the workspace, so toggling it resets
 * every cached query in place: superadmin-only data goes, and the page with anything typed
 * into it stays.
 */
export function TenantQueryProvider({
  children,
  demoMode,
}: {
  readonly children: ReactNode;
  readonly demoMode: boolean;
}): ReactNode {
  const [client] = useState(() => new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: (count, error) => count < 1 && (error instanceof ApiClientTransportError || !(error instanceof ApiClientError) || error.status >= 500),
      },
    },
  }));
  useEffect(() => () => { client.clear(); }, [client]);
  const cachedForDemoMode = useRef(demoMode);
  useEffect(() => {
    if (cachedForDemoMode.current === demoMode) return;
    cachedForDemoMode.current = demoMode;
    void client.resetQueries();
  }, [client, demoMode]);
  return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
}
